import React, { useEffect, useState, useContext } from "react";
import {
  Button,
  Descriptions,
  Row,
  Col,
  Tag,
  Empty,
} from "antd";
import { ArrowLeftOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { Box, Paper, Typography } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";
import { URL as BASE_URL } from "../config";
import { LoaderContext } from "../Components/dashboard";
import toast from "react-hot-toast";

const themeColor = "#4B0082";

export default function UserDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setLoading } = useContext(LoaderContext);
      const [hover, setHover] = useState(false);
  const [user, setUser] = useState(null);
  const [verificationFlags, setVerificationFlags] = useState([]);

 
  const fetchDetails = async () => {
    try {
      const deviceRes = await axios.get(`${BASE_URL}/devicechange/${id}`);
      const { mobileNo, password } = deviceRes.data;

      const userRes = await axios.get(`${BASE_URL}/userregistration`);
      const matchedUser = userRes.data.find(
        (u) => u.mobileNo === mobileNo && u.password === password
      );
      setUser(matchedUser || null);

      const flagRes = await axios.get(`${BASE_URL}/verificationflags`);
      setVerificationFlags(flagRes.data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load user data");
    }
  };

 
  useEffect(() => {
    let isMounted = true;
    const startTime = Date.now();

    setLoading(true);

    const init = async () => {
      if (id) {
        await fetchDetails();
      }

      const elapsed = Date.now() - startTime;
      const remaining = 1500 - elapsed;

      setTimeout(() => {
        if (isMounted) setLoading(false);
      }, remaining > 0 ? remaining : 0);
    };

    init();

    return () => {
      isMounted = false;
    };
  }, [id]);

  
  const getVerificationName = () => {
    if (!user?.fkVerificationFlagId) return "-";
    const flag = verificationFlags.find(
      (f) => f.verificationFlagId === user.fkVerificationFlagId
    );
    return flag ? flag.verificationFlagName : "-";
  };


const handleBack = () => {
  setLoading(true);
  const startTime = Date.now();

  setTimeout(() => {
    const elapsed = Date.now() - startTime;
    const remaining = 1500 - elapsed;
    setTimeout(() => {
      setLoading(false);
      navigate(-1);
    }, remaining > 0 ? remaining : 0);
  }, 0);
};


const handleVerify = async () => {
  const confirm = window.confirm(
    "Are you sure you want to verify this device?"
  );
  if (!confirm) return;

  setLoading(true);
  const startTime = Date.now();

  try {
   
    await axios.patch(`${BASE_URL}/devicechange/${id}`);

    if (user?.mobileNo) {

      const formData = new FormData();
      formData.append("title", "devicechange");
      formData.append("number", user.mobileNo.toString());

      const res = await axios.post(
        `${BASE_URL}/send-whatsapp`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (res.data?.success) {
        toast.success("Device verified & WhatsApp sent successfully!");
      } else {
        toast.error("Device verified but WhatsApp failed!");
      }
    }

    const elapsed = Date.now() - startTime;
    const remaining = 1500 - elapsed;

    setTimeout(() => setLoading(false), remaining > 0 ? remaining : 0);

  } catch (err) {
    console.error(err);

    const elapsed = Date.now() - startTime;
    const remaining = 1500 - elapsed;

    setTimeout(() => {
      setLoading(false);
      toast.error("Verification failed!");
    }, remaining > 0 ? remaining : 0);
  }
};

  if (!user) return <Empty description="User not found" />;

  return (
    <Box sx={{ p: { xs: 0.5, md: 1 } }}>
    
      <Paper elevation={0} sx={{ mb: 2, borderRadius: 2 }}>
        <Row justify="space-between" align="middle" style={{ padding: "10px 16px" }}>
          <Col>
            <Box
                 sx={{
             background: `linear-gradient(90deg, ${themeColor}, #ba70f0)`,
              px: { xs: 2.5, md: 4 },
              py: { xs: 0.8, md: 1.1 },
              borderRadius: "8px 22px 8px 22px",
              boxShadow: "0 6px 16px rgba(255,92,110,0.35)"

            }}
            >
              <Typography
                sx={{
                  fontSize: 18,
                  fontWeight: 900,
                  color: "#fff",
                  letterSpacing: "0.7px",
                  textTransform: "uppercase",
                }}
              >
                User Details
              </Typography>
            </Box>
          </Col>

          <Col>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={handleBack}
              style={{
                borderRadius: 20,
                border: `1px solid ${themeColor}`,
                color: themeColor,
                fontWeight: 600,
              }}
               onMouseOver={(e) => {
          e.currentTarget.style.background = "#fff3e0";
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.background = "#fff";
        }}
            >
              Back
            </Button>
          </Col>
        </Row>
      </Paper>

    
      <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
        <Typography fontWeight={700} sx={{ mb: 2, color: themeColor }}>
          User Information
        </Typography>

        <Descriptions
          bordered
          size="middle"
          column={{ xs: 1, sm: 2 }}
          labelStyle={{ fontWeight: 700, background: "#f7f6f6" }}
        >
          <Descriptions.Item label="User ID">{user.userId}</Descriptions.Item>
          <Descriptions.Item label="User Name">{user.userName || "-"}</Descriptions.Item>
          <Descriptions.Item label="Mobile No">{user.mobileNo || "-"}</Descriptions.Item>
          <Descriptions.Item label="Email">{user.email || "-"}</Descriptions.Item>
          <Descriptions.Item label="Password">{user.password || "-"}</Descriptions.Item>
          <Descriptions.Item label="Birthdate">
            {user.birthdate ? dayjs(user.birthdate).format("DD-MM-YYYY") : "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Device Name">{user.deviceName || "-"}</Descriptions.Item>
          <Descriptions.Item label="Verification Status">
            <Tag color={themeColor}>{getVerificationName()}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Latitude">{user.latitude ?? "-"}</Descriptions.Item>
          <Descriptions.Item label="Longitude">{user.longitude ?? "-"}</Descriptions.Item>
          <Descriptions.Item label="Package Start Date">
            {user.packageStartDate ? dayjs(user.packageStartDate).format("DD-MM-YYYY") : "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Package End Date">
            {user.packageEnddate ? dayjs(user.packageEnddate).format("DD-MM-YYYY") : "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            {user.isActive === 1 ? <Tag color="green">Active</Tag> : <Tag color="red">Inactive</Tag>}
          </Descriptions.Item>
          <Descriptions.Item label="Created Date">
            {user.createdDate ? dayjs(user.createdDate).format("DD-MM-YYYY HH:mm") : "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Address" span={2}>{user.address || "-"}</Descriptions.Item>
        </Descriptions>

        <Row justify="center">
   

<Button
  type="primary"
  size="large"
  icon={<CheckCircleOutlined />}
  style={{
    background: hover ? "#b561f1" : themeColor, 
    borderRadius: 30,
    padding: "0 42px",
    fontWeight: 700,
    marginTop: "20px",
    transition: "background 0.3s",
  }}
  onMouseEnter={() => setHover(true)}
  onMouseLeave={() => setHover(false)}
  onClick={handleVerify}
>
  Verify
</Button>

         
        </Row>
      </Paper>
    </Box>
  );
}
